// © 2026 Buddy Media LLC. All rights reserved.
import { useCallback, useEffect, useMemo, useState } from "react";

import { AdminCard } from "@/components/AdminCard";
import { AdminPageShell } from "@/components/AdminPageShell";
import { ApiError, apiFetch } from "@/lib/api";

// Shadow Orchestrator (T-077, T-087) — admin surface at
// /admin/operations/shadow-orchestrator. Controls the autonomous
// agent: kill-switch, delivery_mode, encrypted Anthropic API key
// (Section A), event filters, conditional deferral, authority scope
// (per MCP action), rate limits, system-prompt versions, notification
// recipients, events log.
//
// T-087 dropped the Zapier hop — delivery now runs direct from the
// backend via either email_relay (renders shadow_orchestrator_alert
// through the existing email pipeline) or anthropic_api (v2, calls the
// Messages API server-side). No more webhook URL to manage.
//
// All settings admin-tunable per Marc's hard requirement — no env-var
// rotations, no redeploy. Anti-loop guard (actor='shadow-orchestrator')
// is the ONE architectural-safety constant; lives in code, not here.

const API_BASE = "/api/admin/shadow-orchestrator";

type SettingsOut = {
  enabled: boolean;
  delivery_mode: "email_relay" | "anthropic_api";
  anthropic_api_key_configured: boolean;
  anthropic_api_key_last_12: string | null;
  notification_emails: string[];
  event_filter_failed: boolean;
  event_filter_cancelled: boolean;
  event_filter_blocked_on_marc: boolean;
  event_filter_warn: boolean;
  event_filter_error: boolean;
  event_filter_complete: boolean;
  defer_when_chat_active: boolean;
  defer_chat_active_window_minutes: number;
  defer_event_types: string[];
  max_writes_per_wakeup: number;
  max_wakeups_per_day: number;
  max_input_tokens_per_day: number;
  max_output_tokens_per_day: number;
  cap_bypass_enabled: boolean;
  cap_bypass_expires_at: string | null;
  current_system_prompt_version: number;
  last_updated_at: string | null;
  last_updated_by: string | null;
  today_wakeups_count: number;
  today_input_tokens: number;
  today_output_tokens: number;
};

type AuthorityRule = {
  action_key: string;
  scope: "unilateral" | "approval_required" | "forbidden";
  reason: string;
  last_changed_at: string | null;
  last_changed_by: string | null;
};

type SystemPromptStub = {
  version: number;
  created_at: string;
  created_by: string;
  notes: string | null;
  active: boolean;
};

type SystemPromptFull = SystemPromptStub & { prompt_text: string };

type EventOut = {
  id: string;
  ts: string;
  event_type: string;
  triggering_task_id: string | null;
  triggering_actor: string | null;
  delivery_mode: string;
  delivery_status: string;
  delivery_error: string | null;
  input_tokens_used: number | null;
  output_tokens_used: number | null;
  wakeup_duration_ms: number | null;
};

type PendingApprovalOut = {
  id: string;
  created_at: string;
  action_key: string;
  agent_reasoning: string;
  expires_at: string;
  status: string;
  decided_at: string | null;
  decided_by: string | null;
};

type TestResultOut = {
  ok: boolean;
  status_code: number | null;
  detail: string;
  extra: Record<string, unknown> | null;
};

export function ShadowOrchestratorPage() {
  const [settings, setSettings] = useState<SettingsOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    apiFetch<SettingsOut>(`${API_BASE}/settings`)
      .then((data) => {
        if (!cancelled) setSettings(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError
            ? `Failed to load settings (HTTP ${err.status})`
            : "Failed to load Shadow Orchestrator settings",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return (
    <AdminPageShell
      title="Shadow Orchestrator"
      description="Autonomous orchestrator agent (T-077, T-087 direct-delivery). Worker stop / warn / error events fire through a kill-switch, event filter, conditional deferral, and rate-limit chain to either the email pipeline (v1, default) or the Anthropic Messages API (v2, one config flip away once the API key is set). No Zapier hop. Anti-loop guard is hard-coded — every other setting lives here."
    >
      <div className="space-y-6">
        {error ? (
          <AdminCard className="border-rose-900/40 bg-rose-950/20">
            <p className="text-sm text-rose-200">{error}</p>
          </AdminCard>
        ) : null}

        {settings === null ? (
          <AdminCard>
            <p className="text-sm text-slate-400">Loading…</p>
          </AdminCard>
        ) : (
          <>
            <KillSwitchAndDeliveryMode settings={settings} onChanged={refresh} />
            <SectionA settings={settings} onChanged={refresh} />
            <SectionB settings={settings} onChanged={refresh} />
            <SectionC settings={settings} onChanged={refresh} />
            <SectionD onChanged={refresh} refreshKey={refreshKey} />
            <SectionE settings={settings} onChanged={refresh} />
            <SectionF onChanged={refresh} refreshKey={refreshKey} />
            <SectionG settings={settings} onChanged={refresh} />
            <SectionH refreshKey={refreshKey} />
          </>
        )}
      </div>
    </AdminPageShell>
  );
}

// ── helpers ─────────────────────────────────────────────────────

async function patchSettings(
  payload: Partial<Record<keyof SettingsOut, unknown>>,
): Promise<SettingsOut> {
  return apiFetch<SettingsOut>(`${API_BASE}/settings`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

function fmtError(err: unknown): string {
  if (err instanceof ApiError) {
    const detail = (err.data as { detail?: string })?.detail;
    return detail ? `${detail} (HTTP ${err.status})` : `HTTP ${err.status}`;
  }
  return err instanceof Error ? err.message : "Unexpected error";
}

// ── kill-switch + delivery_mode ─────────────────────────────────

function KillSwitchAndDeliveryMode({
  settings,
  onChanged,
}: {
  settings: SettingsOut;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const flipKillSwitch = async () => {
    setBusy(true);
    setRowError(null);
    try {
      await patchSettings({ enabled: !settings.enabled });
      onChanged();
    } catch (err) {
      setRowError(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  const setDeliveryMode = async (
    mode: "email_relay" | "anthropic_api",
  ) => {
    setBusy(true);
    setRowError(null);
    try {
      await patchSettings({ delivery_mode: mode });
      onChanged();
    } catch (err) {
      setRowError(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminCard
      className={
        settings.enabled
          ? "border-rose-700/60 bg-rose-950/20"
          : "border-emerald-900/40 bg-emerald-950/15"
      }
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span
              className={
                settings.enabled
                  ? "inline-flex items-center rounded-md border border-rose-500/40 bg-rose-500/20 px-2 py-0.5 text-[11px] font-mono uppercase text-rose-200"
                  : "inline-flex items-center rounded-md border border-emerald-500/40 bg-emerald-500/15 px-2 py-0.5 text-[11px] font-mono uppercase text-emerald-200"
              }
            >
              {settings.enabled ? "● ENABLED" : "○ DISABLED"}
            </span>
            <span className="font-semibold text-white">Kill-switch</span>
          </div>
          <p className="mt-2 text-xs text-slate-400">
            Master gate over every other setting on this page. When OFF the
            MCP-side hook short-circuits before delivery dispatches; the
            agent never wakes.
          </p>
        </div>
        <button
          type="button"
          onClick={flipKillSwitch}
          disabled={busy}
          className={
            settings.enabled
              ? "rounded-md border border-rose-600 bg-rose-700 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-50"
              : "rounded-md bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-400 disabled:opacity-50"
          }
        >
          {busy
            ? "Working…"
            : settings.enabled
              ? "DISABLE shadow orchestrator"
              : "ENABLE shadow orchestrator"}
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-slate-800 pt-4">
        <span className="text-xs uppercase tracking-wide text-slate-400">
          Delivery mode
        </span>
        <select
          value={settings.delivery_mode}
          onChange={(e) =>
            setDeliveryMode(
              e.target.value as "email_relay" | "anthropic_api",
            )
          }
          disabled={busy}
          className="rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-white"
        >
          <option value="email_relay">email_relay (v1)</option>
          <option
            value="anthropic_api"
            disabled={!settings.anthropic_api_key_configured}
          >
            anthropic_api (v2 — requires API key)
          </option>
        </select>
        <span className="text-xs text-slate-500">
          {settings.delivery_mode === "email_relay"
            ? "Backend renders shadow_orchestrator_alert and sends to Section G recipients via the regular email pipeline."
            : "Backend calls Anthropic Messages API server-side using the Section A key (v2 — not yet implemented)."}
        </span>
      </div>

      {rowError ? (
        <p className="mt-3 text-xs text-rose-300">{rowError}</p>
      ) : null}
    </AdminCard>
  );
}

// ── Section A: secrets ──────────────────────────────────────────

function SectionA({
  settings,
  onChanged,
}: {
  settings: SettingsOut;
  onChanged: () => void;
}) {
  return (
    <AdminCard className="space-y-5">
      <SectionHeader
        title="Section A — Master controls"
        subtitle="Anthropic API key (encrypted at rest with BUDDYWATCH_SECRETS_KEY — same posture as /admin/operations/secrets). Never returned plaintext from any GET — admin re-saves via paste. T-087 dropped the Zapier Catch Hook URL: delivery now runs direct from the backend."
      />
      <SecretRow
        label="Anthropic (Claude) API key"
        configured={settings.anthropic_api_key_configured}
        last12={settings.anthropic_api_key_last_12}
        endpoint={`${API_BASE}/secrets/anthropic-api-key`}
        testEndpoint={`${API_BASE}/test-anthropic-key`}
        placeholder="sk-ant-…"
        helperText="Required when delivery_mode = anthropic_api. Format-validated to start with sk-ant-. Test button calls api.anthropic.com /v1/models server-side; the plaintext value never leaves backend memory."
        warnIfMissingAnd={settings.delivery_mode === "anthropic_api"}
        onChanged={onChanged}
      />
    </AdminCard>
  );
}

function SecretRow({
  label,
  configured,
  last12,
  endpoint,
  testEndpoint,
  placeholder,
  helperText,
  warnIfMissingAnd = false,
  onChanged,
}: {
  label: string;
  configured: boolean;
  last12: string | null;
  endpoint: string;
  testEndpoint: string;
  placeholder: string;
  helperText: string;
  warnIfMissingAnd?: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestResultOut | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const masked = configured && last12 ? `••••••••${last12}` : "(not set)";

  const submit = async (clearInstead: boolean) => {
    setBusy(true);
    setRowError(null);
    try {
      await apiFetch(endpoint, {
        method: "PUT",
        body: JSON.stringify({
          value: clearInstead ? "" : value,
        }),
      });
      setValue("");
      setEditing(false);
      setRevealing(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 4000);
      onChanged();
    } catch (err) {
      setRowError(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  const runTest = async () => {
    setBusy(true);
    setRowError(null);
    setTestResult(null);
    try {
      const res = await apiFetch<TestResultOut>(testEndpoint, { method: "POST" });
      setTestResult(res);
    } catch (err) {
      setRowError(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  const missingDangerous = warnIfMissingAnd && !configured;

  return (
    <div
      className={
        missingDangerous
          ? "rounded-lg border border-rose-700/60 bg-rose-950/15 p-4"
          : "rounded-lg border border-slate-800 bg-slate-900/30 p-4"
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={
            configured
              ? "inline-flex items-center rounded-md border border-emerald-500/30 bg-emerald-500/15 px-2 py-0.5 text-[11px] font-mono uppercase text-emerald-300"
              : "inline-flex items-center rounded-md border border-amber-500/30 bg-amber-500/15 px-2 py-0.5 text-[11px] font-mono uppercase text-amber-300"
          }
        >
          {configured ? "Configured" : "Not set"}
        </span>
        <span className="font-semibold text-white">{label}</span>
        <span className="ml-auto font-mono text-xs text-slate-300">{masked}</span>
        {savedFlash ? (
          <span className="text-xs text-emerald-400">Saved.</span>
        ) : null}
      </div>
      <p className="mt-1 text-xs text-slate-400">{helperText}</p>

      {missingDangerous ? (
        <p className="mt-2 text-xs text-rose-200">
          delivery_mode is anthropic_api but no API key is set. Save one or
          switch delivery_mode back to email_relay.
        </p>
      ) : null}

      {rowError ? (
        <p className="mt-2 text-xs text-rose-300">{rowError}</p>
      ) : null}

      {testResult ? (
        <p
          className={
            testResult.ok
              ? "mt-2 text-xs text-emerald-300"
              : "mt-2 text-xs text-rose-300"
          }
        >
          Test: {testResult.detail}
        </p>
      ) : null}

      {!editing ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="rounded-md bg-pink-500 px-3 py-1.5 text-xs font-medium text-white"
          >
            {configured ? "Replace value" : "Set value"}
          </button>
          {configured ? (
            <>
              <button
                type="button"
                onClick={runTest}
                disabled={busy}
                className="rounded-md border border-slate-700 bg-slate-900/40 px-3 py-1.5 text-xs text-slate-200 hover:border-slate-500 disabled:opacity-50"
              >
                Test
              </button>
              <button
                type="button"
                onClick={() => {
                  if (confirm(`Clear ${label}? Any consumer that needs it will fail next call.`))
                    submit(true);
                }}
                disabled={busy}
                className="rounded-md border border-rose-700 px-3 py-1.5 text-xs text-rose-300 hover:bg-rose-950/40 disabled:opacity-50"
              >
                Clear
              </button>
            </>
          ) : null}
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          <input
            type={revealing ? "text" : "password"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-xs text-white placeholder-slate-600"
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => submit(false)}
              disabled={busy || !value.trim()}
              className="rounded-md bg-pink-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setRevealing((r) => !r)}
              className="rounded-md border border-slate-700 bg-slate-900/40 px-3 py-1.5 text-xs text-slate-300 hover:border-slate-500"
            >
              {revealing ? "Hide" : "Reveal"}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setValue("");
                setRowError(null);
              }}
              disabled={busy}
              className="rounded-md border border-slate-800 bg-slate-900/40 px-3 py-1.5 text-xs text-slate-300 hover:border-slate-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <span className="text-[11px] text-slate-500">
              Value is shown only while editing; cleared on save.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section B: event filters ────────────────────────────────────

function SectionB({
  settings,
  onChanged,
}: {
  settings: SettingsOut;
  onChanged: () => void;
}) {
  const flags: Array<{
    key: keyof SettingsOut;
    label: string;
  }> = [
    { key: "event_filter_failed", label: "failed" },
    { key: "event_filter_cancelled", label: "cancelled" },
    { key: "event_filter_blocked_on_marc", label: "blocked_on_marc" },
    { key: "event_filter_warn", label: "warn" },
    { key: "event_filter_error", label: "error" },
    { key: "event_filter_complete", label: "complete (off by default)" },
  ];
  const [busy, setBusy] = useState<string | null>(null);

  const flip = async (key: keyof SettingsOut) => {
    setBusy(key as string);
    try {
      await patchSettings({ [key]: !settings[key] });
      onChanged();
    } finally {
      setBusy(null);
    }
  };

  return (
    <AdminCard>
      <SectionHeader
        title="Section B — Event filters"
        subtitle="The agent only wakes for events you enable here."
      />
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {flags.map((f) => (
          <label
            key={f.key as string}
            className="flex items-center gap-2 rounded-md border border-slate-800 bg-slate-900/30 px-3 py-2 text-sm text-slate-200"
          >
            <input
              type="checkbox"
              checked={Boolean(settings[f.key])}
              onChange={() => flip(f.key)}
              disabled={busy === (f.key as string)}
            />
            <span>{f.label}</span>
          </label>
        ))}
      </div>
    </AdminCard>
  );
}

// ── Section C: deferral ─────────────────────────────────────────

function SectionC({
  settings,
  onChanged,
}: {
  settings: SettingsOut;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [draftWindow, setDraftWindow] = useState(
    settings.defer_chat_active_window_minutes,
  );
  useEffect(() => setDraftWindow(settings.defer_chat_active_window_minutes), [
    settings.defer_chat_active_window_minutes,
  ]);

  const flipMaster = async () => {
    setBusy(true);
    try {
      await patchSettings({ defer_when_chat_active: !settings.defer_when_chat_active });
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const saveWindow = async () => {
    setBusy(true);
    try {
      await patchSettings({ defer_chat_active_window_minutes: draftWindow });
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const toggleDeferType = async (key: string) => {
    const next = settings.defer_event_types.includes(key)
      ? settings.defer_event_types.filter((k) => k !== key)
      : [...settings.defer_event_types, key];
    setBusy(true);
    try {
      await patchSettings({ defer_event_types: next });
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const candidates = [
    "propose_rule_mcp_tool",
    "memory_user_edits",
    "update_task_status_cancelled",
    "update_task_status_supersede",
  ];

  return (
    <AdminCard>
      <SectionHeader
        title="Section C — Conditional deferral"
        subtitle="When chat-Opus is active in q-stream, defer the high-stakes event types so Marc handles them in chat instead of the agent waking."
      />
      <label className="mt-3 flex items-center gap-2 text-sm text-slate-200">
        <input
          type="checkbox"
          checked={settings.defer_when_chat_active}
          onChange={flipMaster}
          disabled={busy}
        />
        Defer high-stakes events when chat-Opus active
      </label>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <span className="text-xs text-slate-400">Active window (min)</span>
        <input
          type="range"
          min={5}
          max={120}
          value={draftWindow}
          onChange={(e) => setDraftWindow(parseInt(e.target.value, 10))}
        />
        <span className="font-mono text-sm text-white">{draftWindow}</span>
        <button
          type="button"
          onClick={saveWindow}
          disabled={
            busy || draftWindow === settings.defer_chat_active_window_minutes
          }
          className="rounded-md bg-pink-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
        >
          Save window
        </button>
      </div>
      <div className="mt-3">
        <p className="text-xs uppercase tracking-wide text-slate-400">
          Defer event types
        </p>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {candidates.map((c) => (
            <label
              key={c}
              className="flex items-center gap-2 rounded-md border border-slate-800 bg-slate-900/30 px-3 py-2 text-xs text-slate-200"
            >
              <input
                type="checkbox"
                checked={settings.defer_event_types.includes(c)}
                onChange={() => toggleDeferType(c)}
                disabled={busy}
              />
              <span className="font-mono">{c}</span>
            </label>
          ))}
        </div>
      </div>
    </AdminCard>
  );
}

// ── Section D: authority rules ──────────────────────────────────

function SectionD({
  refreshKey,
  onChanged,
}: {
  refreshKey: number;
  onChanged: () => void;
}) {
  const [rules, setRules] = useState<AuthorityRule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setError(null);
    apiFetch<AuthorityRule[]>(`${API_BASE}/authority-rules`)
      .then((r) => {
        if (!cancelled) setRules(r);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(fmtError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const update = async (
    rule: AuthorityRule,
    nextScope: AuthorityRule["scope"],
  ) => {
    if (
      rule.scope === "forbidden" &&
      nextScope === "unilateral" &&
      !confirm(
        `Move ${rule.action_key} from FORBIDDEN to UNILATERAL? This permits the agent to take this action without approval. Confirm again to actually do it.`,
      )
    )
      return;
    if (
      rule.scope === "forbidden" &&
      nextScope === "unilateral" &&
      !confirm(`Final confirmation — flip ${rule.action_key} → unilateral?`)
    )
      return;

    try {
      const next = await apiFetch<AuthorityRule[]>(`${API_BASE}/authority-rules`, {
        method: "PUT",
        body: JSON.stringify({
          action_key: rule.action_key,
          scope: nextScope,
          reason: rule.reason,
        }),
      });
      setRules(next);
      onChanged();
    } catch (err) {
      setError(fmtError(err));
    }
  };

  return (
    <AdminCard>
      <SectionHeader
        title="Section D — Authority rules"
        subtitle="Per MCP action: unilateral (act + email Marc), approval_required (draft + token + wait), or forbidden (refuse + email Marc). Defaults are Marc's middle-ground spec; admin-edit takes effect on next wake-up."
      />
      {error ? (
        <p className="mt-2 text-xs text-rose-300">{error}</p>
      ) : null}
      {rules === null ? (
        <p className="mt-3 text-sm text-slate-400">Loading…</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-800 text-xs">
            <thead className="text-slate-400">
              <tr>
                <th className="px-2 py-1.5 text-left">action_key</th>
                <th className="px-2 py-1.5 text-left">scope</th>
                <th className="px-2 py-1.5 text-left">reason</th>
                <th className="px-2 py-1.5 text-left">last changed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rules.map((r) => (
                <tr key={r.action_key}>
                  <td className="px-2 py-1.5 font-mono text-white">
                    {r.action_key}
                  </td>
                  <td className="px-2 py-1.5">
                    <select
                      value={r.scope}
                      onChange={(e) =>
                        update(r, e.target.value as AuthorityRule["scope"])
                      }
                      className={
                        r.scope === "forbidden"
                          ? "rounded-md border border-rose-700 bg-rose-950/30 px-2 py-1 text-rose-200"
                          : r.scope === "approval_required"
                            ? "rounded-md border border-amber-700 bg-amber-950/30 px-2 py-1 text-amber-200"
                            : "rounded-md border border-emerald-700 bg-emerald-950/30 px-2 py-1 text-emerald-200"
                      }
                    >
                      <option value="unilateral">unilateral</option>
                      <option value="approval_required">approval_required</option>
                      <option value="forbidden">forbidden</option>
                    </select>
                  </td>
                  <td className="px-2 py-1.5 text-slate-300">{r.reason}</td>
                  <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">
                    {r.last_changed_by ?? "—"}
                    <br />
                    {r.last_changed_at
                      ? new Date(r.last_changed_at).toISOString().slice(0, 16) + "Z"
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminCard>
  );
}

// ── Section E: rate limits ──────────────────────────────────────

function SectionE({
  settings,
  onChanged,
}: {
  settings: SettingsOut;
  onChanged: () => void;
}) {
  const [draft, setDraft] = useState({
    max_writes_per_wakeup: settings.max_writes_per_wakeup,
    max_wakeups_per_day: settings.max_wakeups_per_day,
    max_input_tokens_per_day: settings.max_input_tokens_per_day,
    max_output_tokens_per_day: settings.max_output_tokens_per_day,
  });
  useEffect(
    () =>
      setDraft({
        max_writes_per_wakeup: settings.max_writes_per_wakeup,
        max_wakeups_per_day: settings.max_wakeups_per_day,
        max_input_tokens_per_day: settings.max_input_tokens_per_day,
        max_output_tokens_per_day: settings.max_output_tokens_per_day,
      }),
    [settings],
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await patchSettings(draft);
      onChanged();
    } catch (e) {
      setErr(fmtError(e));
    } finally {
      setBusy(false);
    }
  };

  const setBypass = async (
    enabled: boolean,
    expiresAt: string | null,
  ) => {
    setBusy(true);
    setErr(null);
    try {
      await patchSettings({
        cap_bypass_enabled: enabled,
        cap_bypass_expires_at: expiresAt,
      });
      onChanged();
    } catch (e) {
      setErr(fmtError(e));
    } finally {
      setBusy(false);
    }
  };

  const quickBypass = (hours: number | "eod") => {
    const now = new Date();
    let expiry: Date;
    if (hours === "eod") {
      expiry = new Date(now);
      expiry.setUTCHours(23, 59, 0, 0);
    } else {
      expiry = new Date(now.getTime() + hours * 3600_000);
    }
    setBypass(true, expiry.toISOString());
  };

  return (
    <AdminCard>
      <SectionHeader
        title="Section E — Rate limits + token budget"
        subtitle="Per-day caps. Cap bypass requires a mandatory expiry (no indefinite bypass)."
      />
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {(
          [
            ["max_writes_per_wakeup", "max writes / wake-up"],
            ["max_wakeups_per_day", "max wake-ups / day"],
            ["max_input_tokens_per_day", "max input tokens / day"],
            ["max_output_tokens_per_day", "max output tokens / day"],
          ] as const
        ).map(([key, label]) => (
          <label
            key={key}
            className="flex items-center justify-between gap-2 rounded-md border border-slate-800 bg-slate-900/30 px-3 py-2 text-xs text-slate-200"
          >
            <span>{label}</span>
            <input
              type="number"
              min={0}
              value={draft[key]}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  [key]: parseInt(e.target.value || "0", 10),
                }))
              }
              className="w-32 rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-right font-mono text-white"
            />
          </label>
        ))}
      </div>
      <button
        type="button"
        onClick={save}
        disabled={busy}
        className="mt-3 rounded-md bg-pink-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
      >
        Save limits
      </button>
      {err ? <p className="mt-2 text-xs text-rose-300">{err}</p> : null}

      <div className="mt-4 rounded-md border border-slate-800 bg-slate-900/30 p-3 text-xs text-slate-300">
        Today: <strong className="text-white">{settings.today_wakeups_count}</strong>{" "}
        wake-ups · {settings.today_input_tokens.toLocaleString()} input ·{" "}
        {settings.today_output_tokens.toLocaleString()} output
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-slate-400">
          Cap bypass
        </span>
        <span
          className={
            settings.cap_bypass_enabled
              ? "inline-flex items-center rounded-md border border-amber-500/40 bg-amber-500/15 px-2 py-0.5 text-[11px] font-mono uppercase text-amber-200"
              : "inline-flex items-center rounded-md border border-slate-700 bg-slate-800/40 px-2 py-0.5 text-[11px] font-mono uppercase text-slate-400"
          }
        >
          {settings.cap_bypass_enabled ? "ACTIVE" : "off"}
        </span>
        {settings.cap_bypass_expires_at ? (
          <span className="text-xs text-slate-400">
            until {new Date(settings.cap_bypass_expires_at).toISOString().slice(0, 16) + "Z"}
          </span>
        ) : null}
        <button
          type="button"
          onClick={() => quickBypass(1)}
          disabled={busy}
          className="rounded-md border border-slate-700 bg-slate-900/40 px-3 py-1 text-xs text-slate-200"
        >
          1h
        </button>
        <button
          type="button"
          onClick={() => quickBypass(4)}
          disabled={busy}
          className="rounded-md border border-slate-700 bg-slate-900/40 px-3 py-1 text-xs text-slate-200"
        >
          4h
        </button>
        <button
          type="button"
          onClick={() => quickBypass("eod")}
          disabled={busy}
          className="rounded-md border border-slate-700 bg-slate-900/40 px-3 py-1 text-xs text-slate-200"
        >
          EOD
        </button>
        {settings.cap_bypass_enabled ? (
          <button
            type="button"
            onClick={() => setBypass(false, null)}
            disabled={busy}
            className="rounded-md border border-rose-700 px-3 py-1 text-xs text-rose-300"
          >
            Disable bypass
          </button>
        ) : null}
      </div>
    </AdminCard>
  );
}

// ── Section F: system prompt versions ───────────────────────────

function SectionF({
  refreshKey,
  onChanged,
}: {
  refreshKey: number;
  onChanged: () => void;
}) {
  const [active, setActive] = useState<SystemPromptFull | null>(null);
  const [versions, setVersions] = useState<SystemPromptStub[] | null>(null);
  const [draftText, setDraftText] = useState("");
  const [draftNotes, setDraftNotes] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setErr(null);
    try {
      const [a, v] = await Promise.all([
        apiFetch<SystemPromptFull>(`${API_BASE}/system-prompt`),
        apiFetch<SystemPromptStub[]>(`${API_BASE}/system-prompt/versions`),
      ]);
      setActive(a);
      setVersions(v);
      if (!editing) setDraftText(a.prompt_text);
    } catch (e) {
      setErr(fmtError(e));
    }
  }, [editing]);

  useEffect(() => {
    reload();
  }, [refreshKey, reload]);

  const saveAsNew = async () => {
    setBusy(true);
    setErr(null);
    try {
      await apiFetch(`${API_BASE}/system-prompt/save`, {
        method: "POST",
        body: JSON.stringify({
          prompt_text: draftText,
          notes: draftNotes || null,
        }),
      });
      setDraftNotes("");
      setEditing(false);
      onChanged();
      await reload();
    } catch (e) {
      setErr(fmtError(e));
    } finally {
      setBusy(false);
    }
  };

  const activate = async (version: number) => {
    if (
      !confirm(
        `Activate version ${version}? This becomes the agent's instruction set on next wake-up.`,
      )
    )
      return;
    setBusy(true);
    setErr(null);
    try {
      await apiFetch(`${API_BASE}/system-prompt/activate`, {
        method: "POST",
        body: JSON.stringify({ version }),
      });
      onChanged();
      await reload();
    } catch (e) {
      setErr(fmtError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminCard>
      <SectionHeader
        title="Section F — System prompt"
        subtitle="Versioned (T-058 ToS pattern). Save creates an inactive version; Activate switches the agent to it on next wake-up."
      />
      {err ? <p className="mt-2 text-xs text-rose-300">{err}</p> : null}
      {active === null || versions === null ? (
        <p className="mt-3 text-sm text-slate-400">Loading…</p>
      ) : (
        <>
          <p className="mt-2 text-xs text-slate-400">
            Active: v{active.version} · created {active.created_at.slice(0, 10)} ·
            by {active.created_by}
          </p>
          <textarea
            className="mt-3 h-72 w-full rounded-md border border-slate-700 bg-slate-950 p-3 font-mono text-xs text-white"
            value={editing ? draftText : active.prompt_text}
            readOnly={!editing}
            onChange={(e) => setDraftText(e.target.value)}
          />
          {editing ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                value={draftNotes}
                onChange={(e) => setDraftNotes(e.target.value)}
                placeholder="Why this revision (notes)"
                className="flex-1 rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-xs text-white"
              />
              <button
                type="button"
                onClick={saveAsNew}
                disabled={busy || !draftText.trim()}
                className="rounded-md bg-pink-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
              >
                Save as new version
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setDraftText(active.prompt_text);
                }}
                disabled={busy}
                className="rounded-md border border-slate-800 px-3 py-1.5 text-xs text-slate-300"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setDraftText(active.prompt_text);
                setEditing(true);
              }}
              className="mt-2 rounded-md border border-slate-700 bg-slate-900/40 px-3 py-1.5 text-xs text-slate-200"
            >
              Edit
            </button>
          )}

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-800 text-xs">
              <thead className="text-slate-400">
                <tr>
                  <th className="px-2 py-1 text-left">v</th>
                  <th className="px-2 py-1 text-left">created</th>
                  <th className="px-2 py-1 text-left">by</th>
                  <th className="px-2 py-1 text-left">notes</th>
                  <th className="px-2 py-1 text-left">status</th>
                  <th className="px-2 py-1 text-left">action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {versions.map((v) => (
                  <tr key={v.version}>
                    <td className="px-2 py-1 font-mono">v{v.version}</td>
                    <td className="px-2 py-1 font-mono text-[10px] text-slate-400">
                      {v.created_at.slice(0, 16)}
                    </td>
                    <td className="px-2 py-1 text-slate-300">{v.created_by}</td>
                    <td className="px-2 py-1 text-slate-400">{v.notes ?? "—"}</td>
                    <td className="px-2 py-1">
                      {v.active ? (
                        <span className="inline-flex items-center rounded-md border border-emerald-500/30 bg-emerald-500/15 px-2 py-0.5 text-[10px] uppercase text-emerald-300">
                          active
                        </span>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      {!v.active ? (
                        <button
                          type="button"
                          onClick={() => activate(v.version)}
                          disabled={busy}
                          className="rounded-md border border-slate-700 px-2 py-0.5 text-[10px] text-slate-200 hover:border-slate-500"
                        >
                          Activate
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AdminCard>
  );
}

// ── Section G: notification recipients ──────────────────────────

function SectionG({
  settings,
  onChanged,
}: {
  settings: SettingsOut;
  onChanged: () => void;
}) {
  const [draft, setDraft] = useState<string[]>(settings.notification_emails);
  useEffect(() => setDraft(settings.notification_emails), [settings.notification_emails]);
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async (next: string[]) => {
    setBusy(true);
    setErr(null);
    try {
      await patchSettings({ notification_emails: next });
      onChanged();
    } catch (e) {
      setErr(fmtError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminCard>
      <SectionHeader
        title="Section G — Notifications"
        subtitle="Recipient list for the v1 email-relay path. At least one valid email required."
      />
      {err ? <p className="mt-2 text-xs text-rose-300">{err}</p> : null}
      <div className="mt-3 space-y-2">
        {draft.map((email, idx) => (
          <div
            key={`${email}-${idx}`}
            className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-900/30 px-3 py-2 text-sm text-slate-200"
          >
            <span className="font-mono">{email}</span>
            <button
              type="button"
              onClick={() => {
                const next = draft.filter((_, i) => i !== idx);
                if (next.length === 0) {
                  setErr("At least one recipient required.");
                  return;
                }
                setDraft(next);
                save(next);
              }}
              disabled={busy}
              className="rounded-md border border-rose-700 px-2 py-0.5 text-[11px] text-rose-300"
            >
              Remove
            </button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <input
          type="email"
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          placeholder="someone@example.com"
          className="flex-1 rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-xs text-white"
        />
        <button
          type="button"
          disabled={!adding.trim() || busy}
          onClick={() => {
            const next = [...draft, adding.trim()];
            setDraft(next);
            setAdding("");
            save(next);
          }}
          className="rounded-md bg-pink-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
        >
          Add recipient
        </button>
      </div>
    </AdminCard>
  );
}

// ── Section H: events log + pending approvals ───────────────────

function SectionH({ refreshKey }: { refreshKey: number }) {
  const [events, setEvents] = useState<EventOut[] | null>(null);
  const [pending, setPending] = useState<PendingApprovalOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filterEventType, setFilterEventType] = useState("");
  const [filterDeliveryStatus, setFilterDeliveryStatus] = useState("");

  const reload = useCallback(async () => {
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterEventType) params.set("event_type", filterEventType);
      if (filterDeliveryStatus) params.set("delivery_status", filterDeliveryStatus);
      params.set("limit", "100");
      const [e, p] = await Promise.all([
        apiFetch<EventOut[]>(`${API_BASE}/events?${params.toString()}`),
        apiFetch<PendingApprovalOut[]>(`${API_BASE}/pending-approvals`),
      ]);
      setEvents(e);
      setPending(p);
    } catch (err) {
      setError(fmtError(err));
    }
  }, [filterEventType, filterDeliveryStatus]);

  useEffect(() => {
    reload();
  }, [refreshKey, reload]);

  const pendingActive = useMemo(
    () => (pending ?? []).filter((p) => p.status === "pending"),
    [pending],
  );

  return (
    <AdminCard>
      <SectionHeader
        title="Section H — Events log + pending approvals"
        subtitle="Reverse-chronological view of every fired hook. Approvals queue is dormant on email-relay v1; activates on the Anthropic API path."
      />

      {pendingActive.length > 0 ? (
        <div className="mt-3 rounded-md border border-amber-700/40 bg-amber-950/20 p-3">
          <p className="text-xs uppercase tracking-wide text-amber-300">
            Pending approvals ({pendingActive.length})
          </p>
          <div className="mt-2 space-y-2">
            {pendingActive.map((p) => (
              <div
                key={p.id}
                className="rounded-md border border-amber-900/50 bg-slate-900/30 p-2 text-xs"
              >
                <div className="font-mono text-amber-200">{p.action_key}</div>
                <p className="mt-1 text-slate-300">{p.agent_reasoning}</p>
                <p className="mt-1 text-[10px] text-slate-500">
                  expires {new Date(p.expires_at).toISOString().slice(0, 16)}Z
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <select
          value={filterEventType}
          onChange={(e) => setFilterEventType(e.target.value)}
          className="rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-white"
        >
          <option value="">any event</option>
          {[
            "failed",
            "cancelled",
            "blocked_on_marc",
            "warn",
            "error",
            "complete",
          ].map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={filterDeliveryStatus}
          onChange={(e) => setFilterDeliveryStatus(e.target.value)}
          className="rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-white"
        >
          <option value="">any status</option>
          {[
            "sent",
            "failed",
            "rate_limited",
            "cap_exceeded",
            "deferred_to_chat",
            "blocked_killswitch",
            "blocked_filter",
            "blocked_antiloop",
          ].map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={reload}
          className="rounded-md border border-slate-700 bg-slate-900/40 px-3 py-1 text-slate-200"
        >
          Refresh
        </button>
      </div>

      {error ? (
        <p className="mt-2 text-xs text-rose-300">{error}</p>
      ) : null}

      <div className="mt-3 overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-800 text-xs">
          <thead className="text-slate-400">
            <tr>
              <th className="px-2 py-1 text-left">ts</th>
              <th className="px-2 py-1 text-left">event</th>
              <th className="px-2 py-1 text-left">task</th>
              <th className="px-2 py-1 text-left">actor</th>
              <th className="px-2 py-1 text-left">delivery</th>
              <th className="px-2 py-1 text-left">tokens</th>
              <th className="px-2 py-1 text-left">duration</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {events === null ? (
              <tr>
                <td colSpan={7} className="px-2 py-3 text-slate-400">
                  Loading…
                </td>
              </tr>
            ) : events.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-2 py-3 text-slate-500">
                  (no events match — try widening the filters)
                </td>
              </tr>
            ) : (
              events.map((e) => (
                <tr key={e.id}>
                  <td className="px-2 py-1 font-mono text-[10px] text-slate-400">
                    {e.ts.slice(11, 19)} {e.ts.slice(0, 10)}
                  </td>
                  <td className="px-2 py-1 font-mono text-white">{e.event_type}</td>
                  <td className="px-2 py-1 font-mono text-slate-300">
                    {e.triggering_task_id ?? "—"}
                  </td>
                  <td className="px-2 py-1 font-mono text-slate-400">
                    {e.triggering_actor ?? "—"}
                  </td>
                  <td className="px-2 py-1">
                    <span
                      className={
                        e.delivery_status === "sent"
                          ? "rounded-md border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-emerald-300"
                          : e.delivery_status === "failed"
                            ? "rounded-md border border-rose-500/30 bg-rose-500/10 px-1.5 py-0.5 text-rose-300"
                            : e.delivery_status.startsWith("blocked")
                              ? "rounded-md border border-slate-700 bg-slate-800/40 px-1.5 py-0.5 text-slate-400"
                              : "rounded-md border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-amber-300"
                      }
                    >
                      {e.delivery_status}
                    </span>
                  </td>
                  <td className="px-2 py-1 font-mono text-[10px] text-slate-400">
                    {(e.input_tokens_used ?? 0) + (e.output_tokens_used ?? 0) || "—"}
                  </td>
                  <td className="px-2 py-1 font-mono text-[10px] text-slate-400">
                    {e.wakeup_duration_ms !== null ? `${e.wakeup_duration_ms}ms` : "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </AdminCard>
  );
}

// ── shared section header ───────────────────────────────────────

function SectionHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <div>
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-200">
        {title}
      </h2>
      {subtitle ? (
        <p className="mt-1 text-xs text-slate-400">{subtitle}</p>
      ) : null}
    </div>
  );
}
